import { Injectable } from '@angular/core';

/**
 * Wrapper léger autour d'IndexedDB pour Dinki — mode offline.
 *
 * <p>Pas de dépendance externe (idb, dexie...) : on utilise l'API native
 * du navigateur enveloppée dans des Promises. Volontairement minimaliste —
 * couvre uniquement ce dont Dinki a besoin : un store par "table" logique,
 * indexée par clé string (référence/uuid), avec opérations put/get/getAll/
 * delete/clear et un timestamp d'écriture pour le TTL côté cache.</p>
 *
 * <p>Schéma actuel :</p>
 * <ul>
 *   <li>{@code cache} — entités lues récemment, indexées par {@code "type:ref"}</li>
 *   <li>{@code mutations} — queue d'écritures offline en attente de replay</li>
 *   <li>{@code meta} — métadonnées (dernière sync, etc.)</li>
 * </ul>
 *
 * <p>Bump {@link #DB_VERSION} pour ajouter un store ou modifier un index.
 * onupgradeneeded gère la migration de manière idempotente.</p>
 */
@Injectable({ providedIn: 'root' })
export class IdbService {
  private static readonly DB_NAME = 'dinki-offline';
  private static readonly DB_VERSION = 1;

  /** Stores exposés. */
  static readonly STORE_CACHE = 'cache';
  static readonly STORE_MUTATIONS = 'mutations';
  static readonly STORE_META = 'meta';

  private dbPromise: Promise<IDBDatabase> | null = null;

  /** Ouvre la base (ou la crée si elle n'existe pas). Mise en cache. */
  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB indisponible (probablement SSR ou navigateur trop ancien)'));
        return;
      }
      const req = indexedDB.open(IdbService.DB_NAME, IdbService.DB_VERSION);

      req.onupgradeneeded = () => {
        const db = req.result;
        // Cache (entités lues) — clé = "type:ref" (ex: "client:UUID")
        if (!db.objectStoreNames.contains(IdbService.STORE_CACHE)) {
          const store = db.createObjectStore(IdbService.STORE_CACHE, { keyPath: 'key' });
          store.createIndex('byType', 'type', { unique: false });
          store.createIndex('byUpdatedAt', 'updatedAt', { unique: false });
        }
        // Mutations en attente — clé auto-incrément, ordonnées par createdAt
        if (!db.objectStoreNames.contains(IdbService.STORE_MUTATIONS)) {
          const store = db.createObjectStore(IdbService.STORE_MUTATIONS, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('byStatus', 'status', { unique: false });
          store.createIndex('byCreatedAt', 'createdAt', { unique: false });
        }
        // Méta — clés libres (lastSyncAt, etc.)
        if (!db.objectStoreNames.contains(IdbService.STORE_META)) {
          db.createObjectStore(IdbService.STORE_META, { keyPath: 'key' });
        }
      };

      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('IndexedDB bloqué — fermez les autres onglets Dinki'));
    });
    return this.dbPromise;
  }

  /** Lit une entrée par clé primaire. */
  async get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const db = await this.open();
    return this.runRequest<T>(db.transaction(store, 'readonly').objectStore(store).get(key));
  }

  /** Liste toutes les entrées (optionnellement filtrées par index). */
  async getAll<T>(
    store: string,
    indexName?: string,
    query?: IDBValidKey | IDBKeyRange,
  ): Promise<T[]> {
    const db = await this.open();
    const tx = db.transaction(store, 'readonly');
    const obj = tx.objectStore(store);
    const src = indexName ? obj.index(indexName) : obj;
    return this.runRequest<T[]>(src.getAll(query));
  }

  /** Crée ou remplace une entrée. */
  async put<T>(store: string, value: T): Promise<IDBValidKey> {
    const db = await this.open();
    return this.runRequest<IDBValidKey>(
      db.transaction(store, 'readwrite').objectStore(store).put(value),
    );
  }

  /** Supprime une entrée par clé primaire. */
  async delete(store: string, key: IDBValidKey): Promise<void> {
    const db = await this.open();
    await this.runRequest<undefined>(
      db.transaction(store, 'readwrite').objectStore(store).delete(key),
    );
  }

  /** Vide un store entier. */
  async clear(store: string): Promise<void> {
    const db = await this.open();
    await this.runRequest<undefined>(
      db.transaction(store, 'readwrite').objectStore(store).clear(),
    );
  }

  /** Compte les entrées d'un store. */
  async count(store: string, indexName?: string, query?: IDBValidKey | IDBKeyRange): Promise<number> {
    const db = await this.open();
    const tx = db.transaction(store, 'readonly');
    const obj = tx.objectStore(store);
    const src = indexName ? obj.index(indexName) : obj;
    return this.runRequest<number>(src.count(query));
  }

  /**
   * Détruit complètement la base — usage debug / reset utilisateur.
   *
   * <p>Ferme d'abord la connexion, sinon le deleteDatabase reste bloqué
   * indéfiniment et l'opération échoue silencieusement.</p>
   */
  async destroy(): Promise<void> {
    if (this.dbPromise) {
      const db = await this.dbPromise;
      db.close();
      this.dbPromise = null;
    }
    await new Promise<void>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        resolve();
        return;
      }
      const req = indexedDB.deleteDatabase(IdbService.DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('Reset IDB bloqué — fermez les autres onglets'));
    });
  }

  // ─── helpers ──────────────────────────────────────────────────────────

  private runRequest<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
}
